import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon, LockIcon, ShieldCheckIcon } from 'lucide-react';
import { CHECKOUT_URL } from './PremiumButton';
interface FormState {
  nombre: string;
  email: string;
  telefono: string;
}
interface FormErrors {
  nombre?: string;
  email?: string;
}
export function CheckoutModal() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>({
    nombre: '',
    email: '',
    telefono: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  useEffect(() => {
    const handler = () => {
      setSubmitted(false);
      setOpen(true);
    };
    window.addEventListener('open-checkout', handler);
    return () => window.removeEventListener('open-checkout', handler);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.nombre.trim()) next.nombre = 'Ingresa tu nombre';
    if (!form.email.trim()) {
      next.email = 'Ingresa tu correo';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'Correo no válido';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Redirect to the external checkout (new tab to escape preview iframe)
    window.open(CHECKOUT_URL, '_blank', 'noopener,noreferrer');
  };
  const close = () => setOpen(false);
  return <AnimatePresence data-id="element-118">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={close} role="dialog" aria-modal="true" aria-label="Finaliza tu compra" data-id="element-119">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative w-full max-w-md bg-forest border border-cream/20 rounded-3xl p-8 shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-120">
            <button onClick={close} aria-label="Cerrar" className="absolute top-5 right-5 text-sand/70 hover:text-cream transition-colors" data-id="element-121">
              <XIcon className="w-5 h-5" data-id="element-122" />
            </button>

            {submitted ? <div className="text-center py-6" data-id="element-123">
                <CheckCircleIcon className="w-16 h-16 text-peach mx-auto mb-5" data-id="element-124" />
                <h3 className="font-serif text-3xl text-cream mb-3" data-id="element-125">
                  ¡Bienvenida a tu cambio!
                </h3>
                <p className="text-sand leading-relaxed" data-id="element-126">
                  Hemos recibido tus datos, {form.nombre.split(' ')[0]}. En unos
                  instantes recibirás en{' '}
                  <span className="text-peach" data-id="element-127">{form.email}</span> el acceso
                  inmediato a tu Plan de 21 Días.
                </p>
                <button onClick={close} className="mt-8 inline-block bg-peach text-forest py-3 px-8 rounded-full font-semibold tracking-wide hover:scale-105 transition-transform" data-id="element-128">
                  Entendido
                </button>
              </div> : <>
                <h3 className="font-serif text-3xl text-cream mb-1 pr-6" data-id="element-129">
                  Finaliza tu compra
                </h3>
                <p className="text-sand/80 text-sm mb-6" data-id="element-130">
                  Acceso inmediato al Plan de 21 Días por{' '}
                  <span className="text-peach font-semibold" data-id="element-131">$6.36 USD</span>
                </p>

                <form onSubmit={handleSubmit} noValidate className="space-y-4" data-id="element-132">
                  <div data-id="element-133">
                    <label htmlFor="nombre" className="block text-sm text-sand mb-1" data-id="element-134">
                      Nombre completo
                    </label>
                    <input id="nombre" type="text" value={form.nombre} onChange={e => setForm({
                ...form,
                nombre: e.target.value
              })} className="w-full bg-forest border border-cream/20 focus:border-peach rounded-lg px-4 py-3 text-cream placeholder-sand/40 outline-none transition-colors" placeholder="Tu nombre" data-id="element-135" />
                    {errors.nombre && <p className="text-burgundy-light text-xs mt-1 text-peach" data-id="element-136">
                        {errors.nombre}
                      </p>}
                  </div>

                  <div data-id="element-137">
                    <label htmlFor="email" className="block text-sm text-sand mb-1" data-id="element-138">
                      Correo electrónico
                    </label>
                    <input id="email" type="email" value={form.email} onChange={e => setForm({
                ...form,
                email: e.target.value
              })} className="w-full bg-forest border border-cream/20 focus:border-peach rounded-lg px-4 py-3 text-cream placeholder-sand/40 outline-none transition-colors" placeholder="tucorreo@ejemplo.com" data-id="element-139" />
                    {errors.email && <p className="text-xs mt-1 text-peach" data-id="element-140">{errors.email}</p>}
                  </div>

                  <div data-id="element-141">
                    <label htmlFor="telefono" className="block text-sm text-sand mb-1" data-id="element-142">
                      Teléfono <span className="text-sand/50" data-id="element-143">(opcional)</span>
                    </label>
                    <input id="telefono" type="tel" value={form.telefono} onChange={e => setForm({
                ...form,
                telefono: e.target.value
              })} className="w-full bg-forest border border-cream/20 focus:border-peach rounded-lg px-4 py-3 text-cream placeholder-sand/40 outline-none transition-colors" placeholder="+1 234 567 890" data-id="element-144" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full bg-peach text-forest py-4 rounded-full font-semibold text-lg tracking-wide hover:scale-[1.02] transition-transform disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-145">
                    {loading ? 'Procesando…' : 'Acceder ahora por $6.36'}
                  </button>
                </form>

                <div className="flex items-center justify-center gap-5 mt-6 text-xs text-sand/70" data-id="element-146">
                  <div className="flex items-center gap-1.5" data-id="element-147">
                    <LockIcon className="w-4 h-4 text-peach" data-id="element-148" />
                    Pago seguro
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-149">
                    <ShieldCheckIcon className="w-4 h-4 text-peach" data-id="element-150" />
                    Garantía 30 días
                  </div>
                </div>
              </>}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}